import React from 'react';
import { Box, Flex, HStack, Button, Switch, Text, Select } from '@chakra-ui/react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { FunnelSimple } from 'phosphor-react';
import moment from 'moment';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import { FilterDrawer } from '../../../../../components/FilterDrawer';
import { MonthNavigator } from '../../../../../components/shared/MonthNavigator';
import { tipoOptions } from '../constants';

interface FilterFormValues {
  codi_emp?: string;
  tipo?: string;
  acumulador?: string;
  apenasDivergencias?: boolean;
}

export type SearchFieldType = 'id' | 'cnpj' | 'razao_social';

const searchFieldOptions: { value: SearchFieldType; label: string; placeholder: string }[] = [
  { value: 'id', label: 'ID', placeholder: 'Buscar por código da empresa' },
  { value: 'cnpj', label: 'CNPJ', placeholder: 'Buscar por CNPJ' },
  { value: 'razao_social', label: 'Razão Social', placeholder: 'Buscar por razão social' },
];

/* eslint-disable no-unused-vars */
interface FilterSectionProps {
  formMethods: UseFormReturn<FilterFormValues>;
  currentDate: moment.Moment;
  usePeriodFilter: boolean;
  onUsePeriodFilterChange: (value: boolean) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectMonth: (year: number, month: number) => void;
  isFilterDrawerOpen: boolean;
  onFilterDrawerOpen: () => void;
  onFilterDrawerClose: () => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onApplyFilters: () => void;
  tipoValue: string;
  onTipoChange: (value: string) => void;
  searchField: SearchFieldType;
  onSearchFieldChange: (value: SearchFieldType) => void;
}
/* eslint-enable no-unused-vars */

export function FilterSection({
  formMethods,
  currentDate,
  usePeriodFilter,
  onUsePeriodFilterChange,
  onPrevMonth,
  onNextMonth,
  onSelectMonth,
  isFilterDrawerOpen,
  onFilterDrawerOpen,
  onFilterDrawerClose,
  hasActiveFilters,
  onClearFilters,
  onApplyFilters,
  tipoValue,
  onTipoChange,
  searchField,
  onSearchFieldChange,
}: FilterSectionProps) {
  const currentSearchOption = searchFieldOptions.find(opt => opt.value === searchField);
  return (
    <Box mb={4} p={4} bg="gray.50" borderRadius="lg">
      <FormProvider {...formMethods}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={4}
        >
          <HStack spacing={0} flex={1} maxW={{ base: '100%', md: '450px' }}>
            <Select
              value={searchField}
              onChange={e => onSearchFieldChange(e.target.value as SearchFieldType)}
              height="38px"
              bg="white"
              borderRightRadius={0}
              borderRight="none"
              w="auto"
              minW="120px"
              fontSize="sm"
              boxShadow="0px 2px 4px #1E293B0D"
            >
              {searchFieldOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <InputForm
              name="codi_emp"
              placeholder={currentSearchOption?.placeholder || 'Buscar...'}
              boxShadow="0px 2px 4px #1E293B0D"
              controlMb={0}
              height="38px"
              borderLeftRadius={0}
            />
          </HStack>

          <HStack spacing={3} flexWrap="wrap">
            <MonthNavigator
              currentDate={currentDate}
              onPrevMonth={onPrevMonth}
              onNextMonth={onNextMonth}
              onSelectMonth={onSelectMonth}
              isDisabled={!usePeriodFilter}
            />
            <HStack spacing={2} bg="white" px={3} py={2} borderRadius="lg" shadow="sm">
              <Switch
                isChecked={usePeriodFilter}
                onChange={e => onUsePeriodFilterChange(e.target.checked)}
                colorScheme="blue"
                size="sm"
              />
              <Text fontSize="sm" color="gray.600">
                Periodo
              </Text>
            </HStack>
            <Button
              onClick={onFilterDrawerOpen}
              leftIcon={<FunnelSimple size={16} />}
              variant="outline"
              colorScheme="blue"
              height="38px"
              size="sm"
              position="relative"
            >
              Filtros
              {hasActiveFilters && (
                <Box
                  w={2}
                  h={2}
                  borderRadius="full"
                  bg="blue.500"
                  position="absolute"
                  top={1}
                  right={1}
                />
              )}
            </Button>
          </HStack>
        </Flex>

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={onFilterDrawerClose}
          title="Filtros Avancados"
          hasActiveFilters={hasActiveFilters}
          onClearFilters={onClearFilters}
          onApplyFilters={onApplyFilters}
          formMethods={formMethods}
          size="md"
        >
          <div className="grid grid-cols-1 gap-4">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                Tipo
              </Text>
              <Select
                value={tipoValue}
                onChange={e => onTipoChange(e.target.value)}
                height="38px"
                bg="white"
              >
                {tipoOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>
            <InputForm
              name="acumulador"
              label="Acumulador"
              placeholder="Codigo do acumulador"
              type="number"
              controlMb={0}
            />
          </div>
        </FilterDrawer>
      </FormProvider>
    </Box>
  );
}
